import config from "config";
import { FC, useEffect, useState } from "react";
import RealtimeConnectionManager from "service/RealtimeConnectionManager";
import { pusherManager } from "utils/pusher";

interface ConnectionStatus {
  instanceId: string;
  connected: boolean;
  state: string;
  error?: string;
}

const PusherConnectionTest: FC = () => {
  const [connectionStatuses, setConnectionStatuses] = useState<
    ConnectionStatus[]
  >([]);
  const [testEvents, setTestEvents] = useState<string[]>([]);

  useEffect(() => {
    const testConnections = async () => {
      const statuses: ConnectionStatus[] = [];

      try {
        const kekPusher = pusherManager.getPusherInstance("kek");
        statuses.push({
          instanceId: "kek",
          connected: kekPusher.connection.state === "connected",
          state: kekPusher.connection.state,
        });

        const visalPusher = pusherManager.getPusherInstance("visal");
        statuses.push({
          instanceId: "visal",
          connected: visalPusher.connection.state === "connected",
          state: visalPusher.connection.state,
        });

        const defaultPusher = pusherManager.getPusherInstance("default");
        statuses.push({
          instanceId: "default",
          connected: defaultPusher.connection.state === "connected",
          state: defaultPusher.connection.state,
        });

        const samplePusher = pusherManager.getPusherInstance("sample");
        statuses.push({
          instanceId: "sample",
          connected: samplePusher.connection.state === "connected",
          state: samplePusher.connection.state,
        });
      } catch (error) {
        console.error("Connection test error:", error);
        statuses.push({
          instanceId: "error",
          connected: false,
          state: "error",
          error: String(error),
        });
      }

      setConnectionStatuses(statuses);
    };

    testConnections();

    const instances = pusherManager.getAllInstances();
    instances.forEach((pusher, instanceId) => {
      pusher.connection.bind("state_change", (states: any) => {
        console.log(`${instanceId} connection state changed:`, states);
        setTestEvents((prev) => [
          ...prev,
          `${instanceId}: ${states.previous} → ${states.current}`,
        ]);
      });

      pusher.connection.bind("connected", () => {
        console.log(`${instanceId} connected`);
        setTestEvents((prev) => [
          ...prev,
          `${instanceId}: Connected successfully`,
        ]);
      });

      pusher.connection.bind("error", (error: any) => {
        console.error(`${instanceId} connection error:`, error);
        const errorMessage = error?.error?.data?.message ||
                            error?.error?.message ||
                            error?.message ||
                            JSON.stringify(error);
        setTestEvents((prev) => [
          ...prev,
          `${instanceId}: Error - ${errorMessage}`,
        ]);
      });
    });

    const manager = RealtimeConnectionManager.getInstance();

    const testCallback = (eventName: string, channelName: string) => {
      console.log(`EVENT RECEIVED: ${eventName} on ${channelName}`);
      setTestEvents((prev) => [
        ...prev,
        `Event received: ${eventName} on ${channelName}`,
      ]);
    };

    const kavaCallbackId = manager.subscribe(
      config.realtime.channels.KAVA_NOTIFICATION,
      [config.realtime.events.BROKER_PUBLISHED_OFFER],
      (eventName) =>
        testCallback(eventName, config.realtime.channels.KAVA_NOTIFICATION),
      "default"
    );

    const kekCallbackId = manager.subscribe(
      config.realtime.channels.KEK_CHANNEL,
      [
        config.realtime.events.BROKER_PUBLISHED_OFFER,
        config.realtime.events.BROKER_THREAD_NOTIFICATION,
        config.realtime.events.REINSURER_READ_THREAD,
        config.realtime.events.REINSURER_THREAD_NOTIFICATION,
      ],
      (eventName) =>
        testCallback(eventName, config.realtime.channels.KEK_CHANNEL),
      "kek"
    );

    const closedCallbackId = manager.subscribe(
      config.realtime.channels.CLOSED_BUSINESS,
      [config.realtime.events.CLOSED_BUSINESS],
      (eventName) =>
        testCallback(eventName, config.realtime.channels.CLOSED_BUSINESS),
      "default"
    );

    const sampleCallbackId = manager.subscribe(
      config.realtime.channels.SAMPLE_CHANNEL,
      [
        config.realtime.events.BROKER_PUBLISHED_OFFER,
        config.realtime.events.BROKER_THREAD_NOTIFICATION,
      ],
      (eventName) =>
        testCallback(eventName, config.realtime.channels.SAMPLE_CHANNEL),
      "sample"
    );

    return () => {
      manager.unsubscribe(
        config.realtime.channels.KAVA_NOTIFICATION,
        [config.realtime.events.BROKER_PUBLISHED_OFFER],
        kavaCallbackId
      );
      manager.unsubscribe(
        config.realtime.channels.KEK_CHANNEL,
        [config.realtime.events.BROKER_PUBLISHED_OFFER],
        kekCallbackId
      );
      manager.unsubscribe(
        config.realtime.channels.CLOSED_BUSINESS,
        [config.realtime.events.CLOSED_BUSINESS],
        closedCallbackId
      );
      manager.unsubscribe(
        config.realtime.channels.SAMPLE_CHANNEL,
        [
          config.realtime.events.BROKER_PUBLISHED_OFFER,
          config.realtime.events.BROKER_THREAD_NOTIFICATION,
        ],
        sampleCallbackId
      );
    };
  }, []);

  const getConnectionStats = () => {
    const manager = RealtimeConnectionManager.getInstance();
    return manager.getConnectionStats();
  };

  return (
    <div className="p-6 bg-gray-50 rounded-lg min-h-screen overflow-y-auto">
      <h2 className="text-xl font-bold mb-4">Pusher Connection Test</h2>

      <div className="mb-6">
        <h3 className="text-lg font-semibold mb-2">Connection Status</h3>
        <div className="space-y-2">
          {connectionStatuses.map((status) => (
            <div
              key={status.instanceId}
              className={`p-3 rounded border ${
                status.connected
                  ? "bg-green-100 border-green-400"
                  : "bg-red-100 border-red-400"
              }`}
            >
              <div className="flex justify-between items-center">
                <span className="font-medium">{status.instanceId}</span>
                <span
                  className={`px-2 py-1 rounded text-sm ${
                    status.connected
                      ? "bg-green-200 text-green-800"
                      : "bg-red-200 text-red-800"
                  }`}
                >
                  {status.state}
                </span>
              </div>
              {status.error && (
                <div className="mt-2 text-sm text-red-600">{status.error}</div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="mb-6">
        <h3 className="text-lg font-semibold mb-2">Channel Subscriptions</h3>
        <pre className="bg-white p-3 rounded border text-sm overflow-auto">
          {JSON.stringify(getConnectionStats(), null, 2)}
        </pre>
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-2">Event Log</h3>
        <div className="bg-white p-3 rounded border max-h-64 overflow-y-auto">
          {testEvents.length === 0 ? (
            <p className="text-gray-500">No events yet...</p>
          ) : (
            testEvents.map((event, index) => (
              <div
                key={index}
                className="text-sm py-1 border-b last:border-b-0"
              >
                <span className="text-gray-500">
                  {new Date().toLocaleTimeString()}
                </span>{" "}
                - {event}
              </div>
            ))
          )}
        </div>
      </div>

      <div className="mt-4">
        <button
          onClick={() => setTestEvents([])}
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          Clear Log
        </button>
      </div>
    </div>
  );
};

export default PusherConnectionTest;
